/**
 * AI Feature Profitability — the killer wedge
 * =============================================
 * Per-feature P&L for B2B SaaS AI features. Costs vs. revenue share,
 * margin per feature, customer-mix drill-down, "is this feature paying
 * for itself?" verdict. Nothing else in the FinOps space ships this.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import BillDetectiveCrossLink from '../components/BillDetectiveCrossLink';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import {
  Bot, ArrowLeft, Sparkles, TrendingUp, TrendingDown, AlertTriangle,
  CheckCircle2, ChevronRight, RefreshCcw, Users, Layers, Beaker,
  ShieldAlert, Plus, Trash2, Trophy, Tags,
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '$0';
  const a = Math.abs(n); const sign = n < 0 ? '-' : '';
  if (a >= 1_000_000) return `${sign}$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${sign}$${(a / 1_000).toFixed(1)}K`;
  return `${sign}$${a.toFixed(0)}`;
};

const VERDICT_META = {
  highly_profitable: { label: 'Highly profitable', color: 'emerald', icon: TrendingUp },
  profitable: { label: 'Profitable', color: 'emerald', icon: CheckCircle2 },
  break_even: { label: 'Break-even', color: 'amber', icon: AlertTriangle },
  losing_money: { label: 'Losing money', color: 'rose', icon: TrendingDown },
  no_data: { label: 'Not enough data', color: 'slate', icon: AlertTriangle },
};

// ─────────────── Token Budgets sub-section ───────────────
// Lets the operator set a per-feature monthly budget with Slack alerts.
// The big differentiator vs. Apptio/ProsperOps/Finout — we don't just
// tell you, we can throttle.
const TokenBudgets = ({ features }) => {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ feature_slug: '', monthly_budget_usd: '', alert_at_pct: 80, hard_cap_action: 'alert_only', throttle_webhook_url: '' });

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/ai-token-budgets/status`, auth());
      setBudgets(data.budgets || []);
    } catch (e) {
      console.debug('Token budgets unavailable:', e?.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const save = async () => {
    if (!draft.feature_slug) return toast.error('Pick a feature');
    if (!draft.monthly_budget_usd || Number(draft.monthly_budget_usd) <= 0) return toast.error('Enter a valid budget');
    try {
      const payload = {
        feature_slug: draft.feature_slug,
        monthly_budget_usd: Number(draft.monthly_budget_usd),
        alert_at_pct: Number(draft.alert_at_pct) || 80,
        hard_cap_action: draft.hard_cap_action,
      };
      if (draft.hard_cap_action === 'throttle' && draft.throttle_webhook_url) {
        payload.throttle_webhook_url = draft.throttle_webhook_url;
      }
      await axios.post(`${API_URL}/ai-token-budgets`, payload, auth());
      toast.success(`Budget saved for ${draft.feature_slug}`);
      setDraft({ feature_slug: '', monthly_budget_usd: '', alert_at_pct: 80, hard_cap_action: 'alert_only', throttle_webhook_url: '' });
      load();
    } catch (e) {
      toast.error('Could not save budget', { description: e?.response?.data?.detail || e?.message });
    }
  };

  const remove = async (slug) => {
    try {
      await axios.delete(`${API_URL}/ai-token-budgets/${slug}`, auth());
      toast.success('Budget removed');
      load();
    } catch (e) { toast.error('Could not delete', { description: e?.message }); }
  };

  const verdict = (b) => {
    const pct = b.pct_consumed ?? 0;
    if (pct >= 100) return { label: 'Exceeded', color: 'text-rose-500', bar: 'bg-rose-500' };
    if (pct >= (b.alert_at_pct ?? 80)) return { label: 'At risk', color: 'text-amber-500', bar: 'bg-amber-500' };
    return { label: 'On track', color: 'text-emerald-500', bar: 'bg-emerald-500' };
  };

  return (
    <Card className="border-emerald-500/30" data-testid="token-budgets-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <ShieldAlert className="h-5 w-5 text-emerald-400" />
          Token Budgets <Badge variant="outline" className="text-[11px] text-emerald-300 border-emerald-500/40 bg-emerald-500/5 ml-1">PROACTIVE</Badge>
        </CardTitle>
        <CardDescription className="text-slate-400">
          Set a monthly cap per feature. We'll Slack-alert at {draft.alert_at_pct}% and (optionally) hit your throttle webhook at 100% — so a viral feature can't bankrupt you overnight.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Existing budgets */}
        {!loading && budgets.length > 0 && (
          <div className="space-y-2" data-testid="token-budgets-list">
            {budgets.map((b) => {
              const v = verdict(b);
              const pct = Math.min(100, b.pct_consumed ?? 0);
              return (
                <div key={b.feature_slug} className="p-3 rounded-md border border-border" data-testid={`token-budget-row-${b.feature_slug}`}>
                  <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                    <div className="flex items-center gap-2 min-w-0">
                      <code className="font-semibold truncate">{b.feature_slug}</code>
                      <Badge variant="outline" className={`text-[11px] ${v.color} border-current/40 bg-current/10`}>{v.label}</Badge>
                      {b.hard_cap_action === 'throttle' && (
                        <Badge variant="outline" className="text-[11px] text-emerald-400 border-emerald-500/40">throttle armed</Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                      <span className="font-mono text-slate-600">
                        {fmtMoney(b.consumed_usd ?? 0)} / {fmtMoney(b.monthly_budget_usd)}
                      </span>
                      <Button size="sm" variant="ghost" onClick={() => remove(b.feature_slug)}
                        className="text-slate-600 hover:text-rose-500 h-7 w-7 p-0" data-testid={`token-budget-delete-${b.feature_slug}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="h-2 bg-slate-200 dark:bg-slate-800 text-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full transition-all ${v.bar}`} style={{ width: `${pct}%` }} />
                  </div>
                  <p className="text-[11px] text-slate-600 mt-1.5">
                    {(b.pct_consumed ?? 0).toFixed(0)}% consumed · projected EoM {fmtMoney(b.projected_eom_usd ?? 0)}
                    {b.last_alert_sent_at && <span> · last alert {new Date(b.last_alert_sent_at).toLocaleDateString()}</span>}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        {/* Inline composer */}
        <div className="border-t border-border pt-4 space-y-2.5" data-testid="token-budget-composer">
          <p className="text-xs uppercase tracking-wider text-slate-600">Add a budget</p>
          <div className="grid grid-cols-1 sm:grid-cols-[1.4fr,1fr,0.8fr,1fr,auto] gap-2">
            <select value={draft.feature_slug}
              onChange={(e) => setDraft({ ...draft, feature_slug: e.target.value })}
              className="bg-background border border-input rounded-md h-9 px-2 text-sm"
              data-testid="token-budget-feature-select">
              <option value="">Pick a feature…</option>
              {features.map((f) => <option key={f.slug} value={f.slug}>{f.name} · {f.slug}</option>)}
            </select>
            <Input type="number" placeholder="Budget USD/mo" min={0} value={draft.monthly_budget_usd}
              onChange={(e) => setDraft({ ...draft, monthly_budget_usd: e.target.value })}
              className="font-mono text-sm" data-testid="token-budget-amount" />
            <Input type="number" min={10} max={100} value={draft.alert_at_pct}
              onChange={(e) => setDraft({ ...draft, alert_at_pct: e.target.value })}
              className="font-mono text-sm" data-testid="token-budget-alert-pct" />
            <select value={draft.hard_cap_action}
              onChange={(e) => setDraft({ ...draft, hard_cap_action: e.target.value })}
              className="bg-background border border-input rounded-md h-9 px-2 text-sm"
              data-testid="token-budget-action">
              <option value="alert_only">Alert only</option>
              <option value="throttle">Throttle webhook</option>
            </select>
            <Button onClick={save} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950" data-testid="token-budget-save-btn">
              <Plus className="h-3.5 w-3.5 mr-1" /> Save
            </Button>
          </div>
          {draft.hard_cap_action === 'throttle' && (
            <Input type="url" placeholder="https://your-api.com/throttle-webhook"
              value={draft.throttle_webhook_url}
              onChange={(e) => setDraft({ ...draft, throttle_webhook_url: e.target.value })}
              className="font-mono text-sm" data-testid="token-budget-webhook-url" />
          )}
          <p className="text-[11px] text-slate-600 italic">
            We POST `{`{feature_slug, consumed_usd, monthly_budget_usd, action: "throttle"}`}` to your webhook when 100% is hit. Idempotent within the month — safe to run on a 5-min cron.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

const AIFeatureProfitability = () => {
  const [loading, setLoading] = useState(true);
  const [features, setFeatures] = useState([]);
  const [summary, setSummary] = useState(null);
  const [selected, setSelected] = useState(null);
  const [drilldown, setDrilldown] = useState(null);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/ai-feature-profitability/overview`, auth());
      setFeatures(data.features || []);
      setSummary(data.summary);
    } catch (e) {
      toast.error('Could not load AI feature profitability', { description: e?.response?.data?.detail });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openDrill = async (feat) => {
    setSelected(feat);
    setDrilldown(null);
    try {
      const { data } = await axios.get(
        `${API_URL}/ai-feature-profitability/features/${feat.slug}/customer-mix`,
        auth(),
      );
      setDrilldown(data);
    } catch (e) {
      toast.error('Could not load customer mix', { description: e?.response?.data?.detail });
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 -mt-px">
      <SEOHead
        title="AI Feature Profitability — Per-feature P&L for SaaS AI"
        description="See which AI features are paying for themselves. Per-customer cost-to-revenue, margin per feature, token spend by endpoint. The only FinOps tool that links AI cost to revenue."
      />
      <div className="p-6 sm:p-8 max-w-6xl mx-auto space-y-6" data-testid="ai-feature-profitability-page" data-tour="ai-feature-grid">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Bot className="h-7 w-7 text-emerald-400" />
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>AI Feature Profitability</BrandTitle></h1>
            <Badge className="bg-emerald-600 text-white border-emerald-500/40 shadow-md">
              <Sparkles className="h-3 w-3 mr-1" />New · No competitor ships this
            </Badge>
          </div>
          <p className="text-slate-600 max-w-3xl">
            Cost-per-AI-feature × revenue-attributable-to-feature = a real <strong>per-feature P&amp;L</strong>. Find out which AI features pay for themselves and which ones are quietly bleeding money. Pick a feature for the customer-mix drill-down.
          </p>
        </div>
        <Button onClick={load} variant="outline" data-testid="refresh-btn">
          <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>

      <BillDetectiveCrossLink
        testid="aifp-bd-crosslink"
        message="AI Feature P&L is LLM-token spend per feature. For all features (compute + token + per-customer) →"
      />

      {/* KPI strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="ai-feature-kpis">
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-slate-600 mb-1">Total monthly AI cost</p>
            <p className="text-2xl font-bold font-mono">{fmtMoney(summary.total_monthly_cost_usd)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-slate-600 mb-1">Net monthly margin</p>
            <p className={`text-2xl font-bold font-mono ${summary.total_monthly_margin_usd < 0 ? 'text-rose-500' : 'text-emerald-500'}`}>{fmtMoney(summary.total_monthly_margin_usd)}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-slate-600 mb-1">Features tracked</p>
            <p className="text-2xl font-bold font-mono">{summary.feature_count}</p>
          </CardContent></Card>
          <Card><CardContent className="p-4">
            <p className="text-xs uppercase tracking-wider text-slate-600 mb-1">Losing money</p>
            <p className={`text-2xl font-bold font-mono ${summary.losing_money_count > 0 ? 'text-rose-500' : ''}`}>{summary.losing_money_count}</p>
          </CardContent></Card>
        </div>
      )}

      {/* Feature list */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Layers className="h-5 w-5 text-emerald-400" />Feature P&amp;L · sorted by margin (worst first)
          </CardTitle>
          <CardDescription className="text-slate-400">Click any row to drill into the customer mix.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-2"><Skeleton className="h-16" /><Skeleton className="h-16" /><Skeleton className="h-16" /></div>
          ) : features.length === 0 ? (
            <p className="text-sm text-slate-600 py-6 text-center">No features defined yet. Add one via <code>POST /api/ai-feature-profitability/features</code>.</p>
          ) : (
            <div className="space-y-2">
              {features.map((f) => {
                const v = VERDICT_META[f.verdict] || VERDICT_META.no_data;
                const Icon = v.icon;
                const colorClass = (
                  v.color === 'emerald' ? 'border-emerald-500/30 bg-emerald-500/5' :
                  v.color === 'rose' ? 'border-rose-500/40 bg-rose-500/5' :
                  v.color === 'amber' ? 'border-amber-500/30 bg-amber-500/5' :
                  'border-border'
                );
                const accent = (
                  v.color === 'emerald' ? 'text-emerald-500' :
                  v.color === 'rose' ? 'text-rose-500' :
                  v.color === 'amber' ? 'text-amber-500' :
                  'text-slate-300'
                );
                return (
                  <button
                    type="button"
                    key={f.slug}
                    onClick={() => openDrill(f)}
                    className={`w-full text-left p-4 rounded-md border transition-all hover:shadow-md ${colorClass} ${selected?.slug === f.slug ? 'ring-2 ring-cyan-500/40' : ''}`}
                    data-testid={`feature-row-${f.slug}`}
                  >
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr,auto,auto,auto,auto] items-center gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <p className="font-semibold truncate">{f.name}</p>
                          <Badge variant="outline" className={`text-[11px] ${accent} border-current/40 bg-current/5`}>
                            <Icon className="h-3 w-3 mr-1" />{v.label}
                          </Badge>
                        </div>
                        <p className="text-xs text-slate-600 truncate">
                          <code>{f.slug}</code> · {f.model || '—'} · {(f.monthly_calls / 1000).toFixed(1)}K calls/mo
                        </p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-slate-600">Cost</p>
                        <p className="font-mono font-bold text-rose-400">{fmtMoney(f.monthly_cost_usd)}</p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-slate-600">Revenue</p>
                        <p className="font-mono font-bold text-emerald-400">{fmtMoney(f.monthly_revenue_share_usd)}</p>
                      </div>
                      <div className="text-right text-xs">
                        <p className="text-slate-600">Margin</p>
                        <p className={`font-mono font-bold ${accent}`}>{fmtMoney(f.monthly_margin_usd)}</p>
                      </div>
                      <ChevronRight className="h-4 w-4 text-slate-600" />
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Drill-down */}
      {selected && (
        <Card data-testid="drilldown-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Users className="h-5 w-5 text-emerald-400" />
              Top customers by usage of <code>{selected.slug}</code>
            </CardTitle>
            <CardDescription className="text-slate-400">Sorted by margin contribution (worst first).</CardDescription>
          </CardHeader>
          <CardContent>
            {drilldown === null ? (
              <Skeleton className="h-32" />
            ) : drilldown.customers.length === 0 ? (
              <p className="text-sm text-slate-600 py-6 text-center">No customer-mix telemetry connected yet for this feature.</p>
            ) : (
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-slate-600 uppercase">
                  <tr>
                    <th className="p-2">Customer</th>
                    <th className="p-2 text-right">Calls/mo</th>
                    <th className="p-2 text-right">Cost/mo</th>
                    <th className="p-2 text-right">MRR</th>
                    <th className="p-2 text-right">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {drilldown.customers.map((c, i) => (
                    <tr key={i} data-testid={`drilldown-row-${i}`} className={c.estimated_margin_usd < 0 ? 'bg-rose-500/5' : ''}>
                      <td className="p-2 font-medium">{c.customer}</td>
                      <td className="p-2 text-right font-mono">{(c.calls_per_month).toLocaleString()}</td>
                      <td className="p-2 text-right font-mono text-rose-400">{fmtMoney(c.estimated_cost_usd)}</td>
                      <td className="p-2 text-right font-mono text-emerald-400">{fmtMoney(c.mrr_usd)}</td>
                      <td className={`p-2 text-right font-mono font-bold ${c.estimated_margin_usd < 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                        {fmtMoney(c.estimated_margin_usd)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Token Budgets — proactive cap + Slack/throttle */}
      <TokenBudgets features={features} />

      {/* Cross-links to sibling FinOps tools */}
      <Card>
        <CardHeader><CardTitle className="text-base">Other FinOps tools</CardTitle></CardHeader>
        <CardContent className="flex gap-2 flex-wrap">
          <Link to="/engineer-savings-wall"><Button size="sm" variant="outline" data-testid="cross-link-engineer-wall"><Trophy className="w-3.5 h-3.5 mr-1.5" />Engineer Savings Wall</Button></Link>
          <Link to="/tag-drift"><Button size="sm" variant="outline" data-testid="cross-link-tag-drift"><Tags className="w-3.5 h-3.5 mr-1.5" />Tag-Drift Detective</Button></Link>
          <Link to="/customer-pl"><Button size="sm" variant="outline">Customer P&amp;L</Button></Link>
          <Link to="/commitment-coach"><Button size="sm" variant="outline">Commitment Coach</Button></Link>
        </CardContent>
      </Card>

      {/* How this works */}
      <Card className="bg-gradient-to-br from-emerald-500/5 to-transparent border-emerald-500/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2"><Beaker className="h-5 w-5 text-emerald-400" />How this works (transparent on purpose)</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2 text-slate-600">
          <p><strong className="text-foreground">Cost</strong> = <code>avg_cost_per_call × monthly_calls</code>. Editable per feature; nothing hidden.</p>
          <p><strong className="text-foreground">Revenue share</strong> = <code>(total_arr / 12) × usage_share_of_arr_pct</code>. The share is your honest estimate of how much of your customers' subscription revenue this feature drives. Use heuristics (engagement %, time-spent, perceived value); refine as you learn.</p>
          <p><strong className="text-foreground">Verdict</strong> is purely a function of ROI: <code>highly_profitable ≥ 200%</code>, <code>profitable ≥ 25%</code>, <code>break_even ±25%</code>, otherwise <code>losing_money</code>.</p>
          <p className="text-xs italic pt-2 border-t border-border">Why we built this: every B2B SaaS founder we talk to has the same blind spot — they ship 4-8 AI features and have zero per-feature unit economics. Datadog has APM-level traces but it's an enterprise sale. We give you 80% of the answer in 5 minutes.</p>
          <div className="pt-2 flex gap-2">
            <Link to="/customer-pl"><Button size="sm" variant="outline">Customer P&amp;L →</Button></Link>
            <Link to="/savings-calculator"><Button size="sm" variant="outline">Savings Calculator →</Button></Link>
          </div>
        </CardContent>
      </Card>
    </div>
      </div>
  );
};

export default AIFeatureProfitability;
